import React, { useState } from 'react';
import clsx from 'clsx';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import MaskedInput from 'react-input-mask';
import styled from '@emotion/styled';

import notify from 'notify';
import { sendEmail, FormValues } from 'api';
import { Button, Input, Checkbox } from 'components';

interface ContactFormProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  formTitle?: React.ReactNode;
  modal?: boolean;
  extended?: boolean;
  subject?: string;
  buttonText?: React.ReactNode;
  onSubmit?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  formTitle = '',
  modal = false,
  extended,
  subject = 'Contact Request',
  buttonText = 'Send',
  onSubmit
}) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      body: ''
    }
  });
  const [acceptTerms, setAcceptTerms] = useState(false);

  const submit = async (values: FormValues) => {
    try {
      await sendEmail({ ...values, subject });
      notify('Message was sent successfully.');
      onSubmit?.();
      reset();
    } catch (err) {
      notify(err.message);
    }
  };

  return (
    <StyledContactForm className={clsx('contact-form', { modal })}>
      {formTitle && <h4 className="contact-form__title">{formTitle}</h4>}
      <form className="contact-form__form" onSubmit={handleSubmit(submit)}>
        <Input
          placeholder={t('contact_form.name')}
          className="contact-form__input"
          {...register('name', {
            required: 'Required',
            maxLength: {
              value: 50,
              message: 'Max length exceeded'
            },
            pattern: {
              value: /^[a-z0-9 ,.'-]+$/i,
              message: 'Invalid name'
            }
          })}
          error={errors.name}
        />
        <Input
          placeholder={t('contact_form.email')}
          className="contact-form__input"
          {...register('email', {
            required: 'Required',
            maxLength: {
              value: 254,
              message: `Email can not exceed 254 characters`
            },
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email format'
            }
          })}
          error={errors.email}
        />
        <Controller
          control={control}
          name="phone"
          rules={{
            required: 'Required',
            validate: value => !value?.includes('_') || 'Required'
          }}
          render={({ field }) => (
            <Input
              as={MaskedInput}
              mask="(999) 999-9999"
              placeholder={t('contact_form.phone_number')}
              error={errors.phone}
              className="contact-form__input"
              {...field}
            />
          )}
        />
        {extended && (
          <>
            <Input
              as="textarea"
              className="contact-form__textarea"
              placeholder={t('contact_form.your_message')}
              {...register('body', {
                required: 'Required'
              })}
              error={errors.body}
            />
          </>
        )}
        <Checkbox
          className="contact-form__checkbox"
          checked={acceptTerms}
          onChange={e => setAcceptTerms(e.target.checked)}
          label={
            <>
              I agree to the{' '}
              <a href="privacy" target="blank" rel="noreferrer">
                Privacy Policy
              </a>
            </>
          }
        />
        <Button className="contact-form__button" disabled={isSubmitting || !acceptTerms}>
          {buttonText}
        </Button>
      </form>
    </StyledContactForm>
  );
};

const StyledContactForm = styled.div`
  .contact-form {
    &__form {
      width: 100%;
      text-align: start;
    }

    &__input {
      margin: 0 0 12px;
      width: 100%;
    }
    &__checkbox {
      label a {
        color: ${props => props.theme.colors.primary};
        text-decoration: none;
      }
    }
    &__button {
      margin-top: 12px;
      width: 240px;
    }
    &__textarea {
      width: 100%;
      margin: 0 0 12px;
      .input__component {
        min-height: 112px;
        resize: none;
      }
    }
    &__title {
      width: 100%;
      max-width: 260px;
      font-weight: 500;
      font-size: 18px;
      line-height: 26px;
      margin: 0 0 32px;
      text-align: left;
    }
    &__error {
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 500;
      font-size: 12px;
      line-height: 24px;
      color: ${props => props.theme.colors.red};
      margin-bottom: 8px;

      .icon {
        margin-right: 4px;
      }
    }
  }

  &.modal {
    .contact-form__title {
      text-align: center;
    }
    .contact-form__button {
      margin-top: 24px;
      display: flex;
      justify-content: center;
      margin-left: auto;
      margin-right: auto;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    width: 100%;
    .contact-form {
      &__button {
        width: 100%;
      }
    }
  }
`;

export default ContactForm;
